/**
 * Client-side copy of the worker's `lib/qualify.ts` for the Settings → Screening
 * "Try it" preview. Same semantics (brief §3: code decides, people make the
 * final call): knockouts first, then required answers, then weighted score vs
 * thresholds. Keep in step with the backend — the preview must never promise a
 * result the worker would not produce.
 */
import type { Qualification } from '@/features/pipeline/types';
import { describeRule as describeRuleWith, questionMap } from './reasons';
import type { QuestionType, RuleOp, RuleValue, ScreeningConfig, ScreeningQuestion } from './types';

export type QualifyResult = {
  result: Exclude<Qualification, null>;
  score: number;
  /** Sum of every positive scoring rule — the "/ max" in the UI. */
  max: number;
  reasons: string[];
};

export type Answers = Record<string, unknown>;

const YES = new Set(['yes', 'y', 'true', '1', 'yeah', 'yep']);
const NO = new Set(['no', 'n', 'false', '0', 'nope']);

export function isMissing(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
}

/** "yes"/"no", "3", "3.5" and friends become the typed value the rules expect. */
export function coerce(v: unknown, type: QuestionType | undefined): unknown {
  if (isMissing(v)) return undefined;
  if (type === 'boolean') {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    const s = String(v).trim().toLowerCase();
    if (YES.has(s)) return true;
    if (NO.has(s)) return false;
    return undefined;
  }
  if (type === 'number') {
    if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
    if (typeof v === 'boolean') return v ? 1 : 0;
    const n = Number(String(v).replace(/[,$\s]/g, '').replace(/^(\d+)\+$/, '$1'));
    return Number.isFinite(n) ? n : undefined;
  }
  // text / choice / unknown type: trimmed string, but keep arrays as they are.
  if (Array.isArray(v)) return v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v).trim();
}

function asNumber(v: unknown): number | undefined {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (typeof v === 'string') {
    const n = Number(v.trim());
    return v.trim() !== '' && Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function norm(v: unknown): string {
  return String(v).trim().toLowerCase();
}

function looseEq(a: unknown, b: unknown): boolean {
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    const ab = coerce(a, 'boolean');
    const bb = coerce(b, 'boolean');
    return ab !== undefined && ab === bb;
  }
  const an = asNumber(a);
  const bn = asNumber(b);
  if (an !== undefined && bn !== undefined) return an === bn;
  return norm(a) === norm(b);
}

function listOf(value: RuleValue): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') return value.split(',').map((s) => s.trim()).filter(Boolean);
  return value === null ? [] : [value];
}

/** Evaluates one rule op. Missing answers only match `falsy`. */
export function matches(answer: unknown, op: RuleOp, value: RuleValue): boolean {
  const missing = isMissing(answer);
  switch (op) {
    case 'falsy':
      return missing || answer === false || answer === 0 || norm(answer) === 'false' || NO.has(norm(answer));
    case 'truthy':
      return !missing && answer !== false && answer !== 0 && !NO.has(norm(answer)) && norm(answer) !== 'false';
  }
  if (missing) return false;
  switch (op) {
    case 'eq':
      return looseEq(answer, value);
    case 'neq':
      return !looseEq(answer, value);
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = asNumber(answer);
      const b = asNumber(value);
      if (a === undefined || b === undefined) return false;
      return op === 'gt' ? a > b : op === 'gte' ? a >= b : op === 'lt' ? a < b : a <= b;
    }
    case 'in':
      return listOf(value).some((x) => looseEq(answer, x));
    case 'contains': {
      if (Array.isArray(answer)) return answer.some((x) => looseEq(x, value));
      return value !== null && norm(answer).includes(norm(value));
    }
  }
  return false;
}

/** Coerces every answer by its question's type; unknown keys pass through trimmed. */
export function coerceAnswers(questions: ScreeningQuestion[], raw: Answers): Answers {
  const types = new Map(questions.map((q) => [q.key, q.type]));
  const out: Answers = {};
  for (const [k, v] of Object.entries(raw)) {
    const c = coerce(v, types.get(k));
    if (c !== undefined) out[k] = c;
  }
  return out;
}

export function maxScore(config: Pick<ScreeningConfig, 'rules'>): number {
  return config.rules.scoring.reduce((sum, r) => sum + Math.max(0, r.points), 0);
}

export function qualify(config: ScreeningConfig, rawAnswers: Answers): QualifyResult {
  const answers = coerceAnswers(config.questions, rawAnswers);
  const max = maxScore(config);
  const reasons: string[] = [];

  for (const k of config.rules.knockouts) {
    if (matches(answers[k.field], k.op, k.value)) reasons.push(k.reason || `Knockout: ${k.field}`);
  }
  if (reasons.length) return { result: 'not_qualified', score: 0, max, reasons };

  const missing = config.questions.filter((q) => q.required && isMissing(answers[q.key]));

  let score = 0;
  for (const s of config.rules.scoring) {
    if (matches(answers[s.field], s.op, s.value)) {
      score += s.points;
      reasons.push(`${s.points >= 0 ? '+' : ''}${s.points} — ${describeRule(config, s.field, s.op, s.value)}`);
    }
  }

  if (missing.length) {
    return {
      result: 'needs_review',
      score,
      max,
      reasons: [...missing.map((q) => `Missing required answer: ${q.prompt || q.key}`), ...reasons],
    };
  }

  const { qualified_min, review_min } = config.rules;
  if (score >= qualified_min) return { result: 'qualified', score, max, reasons: [`Score ${score} meets the qualified mark of ${qualified_min}`, ...reasons] };
  if (score >= review_min) return { result: 'needs_review', score, max, reasons: [`Score ${score} is between ${review_min} and ${qualified_min}`, ...reasons] };
  return { result: 'not_qualified', score, max, reasons: [`Score ${score} is under the review mark of ${review_min}`, ...reasons] };
}

/** "How many years have you worked as a technician is at least 2" — same words as `features/screening/reasons.ts`. */
export function describeRule(config: Pick<ScreeningConfig, 'questions'>, field: string, op: RuleOp, value: RuleValue): string {
  return describeRuleWith(questionMap(config), field, op, value);
}
